// Tokens that live in our URLs, kept out of Sentry (review 2026-10-23, finding 5).
//
// A link in a URL is a working credential: the order-summary `?t=` reads one order
// for 30 days (server/orderSummaryToken.ts) and is the link in every Clyde
// confirmation; `?token=` is the PIN-reset magic link, email verification and the
// set-password invite; `?code=` is the QuickBooks OAuth code; the onboarding intake
// token is a path segment (`/onboard/:token`, `/api/onboarding/:token`). The Sentry
// SDKs put page and request URLs on every event (`request.url`, `query_string`,
// the Referer header), on fetch/xhr/navigation breadcrumbs, and on spans
// (`url.full`, `http.target`, `http.url`). Both `Sentry.init` calls
// (client/src/main.tsx, server/instrument.ts) run these hooks so none of that
// leaves with a token in it. Same concern as server/csp.ts never logging a
// report's full URL.

export const REDACTED = "[redacted]";

/** Query parameters whose value is a credential. */
const TOKEN_PARAMS = ["t", "token", "code"];

const TOKEN_PARAM = new RegExp(`(^|[?&;#])(${TOKEN_PARAMS.join("|")})=([^&#;]*)`, "gi");
// The intake token is the segment after `/onboard/` or `/api/onboarding/`
// (never `/api/super-admin/onboarding`, which has no token in it).
const INTAKE_PATH = /(\/onboard\/|\/api\/onboarding\/)([^/?#&]+)/g;

/**
 * Replaces each token value in a URL, path or bare query string with `[redacted]`.
 * Works on absolute and relative URLs and leaves everything else byte-for-byte as
 * it was (no re-encoding, no reordering).
 */
export function scrubUrlTokens(value: string): string {
  return value
    .replace(TOKEN_PARAM, (_m, lead: string, key: string, v: string) =>
      v === "" ? `${lead}${key}=` : `${lead}${key}=${REDACTED}`,
    )
    .replace(INTAKE_PATH, (_m, prefix: string) => `${prefix}${REDACTED}`);
}

function scrubIfString<T>(value: T): T {
  return (typeof value === "string" ? scrubUrlTokens(value) : value) as T;
}

const TOKEN_PARAM_SET = new Set(TOKEN_PARAMS);

/** `query_string` is a string, `[key, value]` pairs, or an object, depending on the SDK. */
function scrubQueryString(qs: unknown): unknown {
  if (typeof qs === "string") return scrubUrlTokens(qs);
  if (Array.isArray(qs)) {
    return qs.map((pair) =>
      Array.isArray(pair) && typeof pair[0] === "string" && TOKEN_PARAM_SET.has(pair[0].toLowerCase())
        ? [pair[0], REDACTED]
        : pair,
    );
  }
  if (qs && typeof qs === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(qs)) out[k] = TOKEN_PARAM_SET.has(k.toLowerCase()) ? REDACTED : v;
    return out;
  }
  return qs;
}

/** Span / trace attributes that carry a URL or a piece of one. */
const URL_ATTRIBUTES = ["url", "url.full", "url.path", "url.query", "http.target", "http.url", "http.query"];

function scrubAttributes(data: unknown): void {
  if (!data || typeof data !== "object") return;
  const record = data as Record<string, unknown>;
  for (const key of URL_ATTRIBUTES) {
    if (key in record) record[key] = scrubIfString(record[key]);
  }
}

// Structural shapes of the parts of a Sentry event we touch — the same in
// @sentry/node and @sentry/react, so one hook serves both without importing either.
interface ScrubbableRequest {
  url?: string;
  query_string?: unknown;
  headers?: Record<string, string>;
}

interface ScrubbableEvent {
  request?: ScrubbableRequest;
  transaction?: string;
  contexts?: { trace?: { data?: Record<string, unknown> } & Record<string, unknown> } & Record<string, unknown>;
  spans?: Array<{ description?: string; data?: Record<string, unknown> }>;
}

interface ScrubbableBreadcrumb {
  message?: string;
  data?: Record<string, unknown>;
}

/** `beforeSend` / `beforeSendTransaction` for both SDKs. Mutates and returns the event. */
export function scrubSentryEvent<E extends ScrubbableEvent>(event: E): E {
  const request = event.request;
  if (request) {
    if (typeof request.url === "string") request.url = scrubUrlTokens(request.url);
    if (request.query_string !== undefined) request.query_string = scrubQueryString(request.query_string);
    if (request.headers) {
      for (const name of Object.keys(request.headers)) {
        if (name.toLowerCase() === "referer") request.headers[name] = scrubUrlTokens(request.headers[name]);
      }
    }
  }
  if (typeof event.transaction === "string") event.transaction = scrubUrlTokens(event.transaction);
  scrubAttributes(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) {
    if (typeof span.description === "string") span.description = scrubUrlTokens(span.description);
    scrubAttributes(span.data);
  }
  return event;
}

/** `beforeBreadcrumb` for both SDKs: fetch/xhr `url`, navigation `from` / `to`. */
export function scrubSentryBreadcrumb<B extends ScrubbableBreadcrumb>(breadcrumb: B): B {
  const data = breadcrumb.data;
  if (data) {
    for (const key of ["url", "from", "to"]) {
      if (key in data) data[key] = scrubIfString(data[key]);
    }
  }
  if (typeof breadcrumb.message === "string") breadcrumb.message = scrubUrlTokens(breadcrumb.message);
  return breadcrumb;
}
